// The hero: the real timeline of our first film, edge to edge. On load a
// playhead runs across it and the clips appear behind it while the counter
// runs to 5,487. After that the playhead follows the pointer, and the layer
// chips light one kind of clip at a time. Every block is a clip that was placed.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CRWIcon } from '../components/icons';
import { MetalBtn } from '../components/primitives';
import { GROUP_COLOR, clipsBefore, drawTimeline, useTimeline, type Group } from '../components/timeline';
import { scrollToHash } from './nav';
import '../styles/hero.css';
import '../styles/timeline-page.css';

gsap.registerPlugin(ScrollTrigger);

// The Google Fonts CSS loads async, so it can land AFTER window load; the font
// swap then shifts layout and every ScrollTrigger start measured before it goes
// stale. Refresh once webfonts are done (global: fixes all ScrollTrigger users).
if (typeof document !== 'undefined' && document.fonts?.ready) {
  void document.fonts.ready.then(() => ScrollTrigger.refresh());
}

// clip counts per layer, from the sequence (the same numbers as the project bins)
const LAYERS: { label: string; n: string; g: Group }[] = [
  { label: 'Footage', n: '957', g: 'ft' },
  { label: 'Interviews', n: '122', g: 'ji' },
  { label: 'AI scenes', n: '245', g: 'ai' },
  { label: 'Graphics', n: '1,252', g: 'gfx' },
  { label: 'Sound', n: '1,284', g: 'sfx' },
  { label: 'Music', n: '57', g: 'mus' },
  { label: 'Subtitles', n: '969', g: 'sub' },
];

export const tcOf = (tenths: number) => {
  const s = Math.floor(tenths / 10);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

export function HeroTimeline() {
  const data = useTimeline();
  const rootRef = useRef<HTMLElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const clipRef = useRef<HTMLDivElement | null>(null);
  const cvRef = useRef<HTMLCanvasElement | null>(null);
  const headRef = useRef<HTMLSpanElement | null>(null);
  const countRef = useRef<HTMLElement | null>(null);
  const tcRef = useRef<HTMLSpanElement | null>(null);
  const revealed = useRef(false);
  const [lit, setLit] = useState<Group | null>(null); // the layer a chip is showing
  const [pinned, setPinned] = useState<Group | null>(null); // a chip that was clicked stays

  // put the playhead at p (0..1 of the film) and read the clips placed by then
  const setHead = (p: number) => {
    const head = headRef.current;
    if (!head || !data) return;
    head.style.left = `${(p * 100).toFixed(3)}%`;
    head.dataset.flip = p > 0.82 ? '1' : '';
    const t = p * data.end;
    if (countRef.current) countRef.current.textContent = clipsBefore(data, t + 1).toLocaleString('en-US');
    if (tcRef.current) tcRef.current.textContent = tcOf(t);
  };

  // paint, and repaint when the lit layer or the size changes
  const only = lit ?? pinned;
  useEffect(() => {
    const cv = cvRef.current;
    if (!cv || !data) return;
    const paint = () => drawTimeline(cv, data, 0, data.end, only ? [only] : null);
    paint();
    const ro = new ResizeObserver(paint);
    ro.observe(cv);
    return () => ro.disconnect();
  }, [data, only]);

  // the opening run: the playhead crosses the film once and the clips appear behind it
  useEffect(() => {
    if (!data || revealed.current) return;
    const clip = clipRef.current;
    if (!clip) return;
    const done = () => { revealed.current = true; clip.style.clipPath = ''; setHead(1); };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { done(); return; }
    const o = { p: 0 };
    const tw = gsap.to(o, {
      p: 1, duration: 2.8, delay: 0.35, ease: 'power2.inOut',
      onStart: () => rootRef.current?.setAttribute('data-run', ''),
      onUpdate: () => { clip.style.clipPath = `inset(0 ${((1 - o.p) * 100).toFixed(3)}% 0 0)`; setHead(o.p); },
      onComplete: done,
    });
    clip.style.clipPath = 'inset(0 100% 0 0)';
    return () => { tw.kill(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // after the run the playhead follows the pointer, and rests at the end
  const onMove = (e: React.PointerEvent) => {
    if (!revealed.current || !boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    setHead(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
  };
  const onLeave = () => { if (revealed.current) setHead(1); };

  // entrance of the words, and the timeline easing back as the page scrolls on
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      gsap.timeline({ defaults: { ease: 'power4.out' } })
        .from('.onb-legend', { opacity: 0, y: 10, duration: 0.8 }, 0.5)
        .from('.onb-line', { yPercent: 110, duration: 0.95, stagger: 0.12 }, 0.25)
        .from('.onb-side', { opacity: 0, y: 16, duration: 0.8 }, 0.75);
      gsap.to('.onb-tl', {
        opacity: 0.3, ease: 'none',
        scrollTrigger: { trigger: rootRef.current, start: '70% center', end: 'bottom top', scrub: true },
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  const go = (hash: string) => (e: React.MouseEvent) => { e.preventDefault(); scrollToHash(hash); };

  return (
    <header className="onb-hero" ref={rootRef}>
      <div className="onb-tl" ref={boxRef} onPointerMove={onMove} onPointerLeave={onLeave}>
        <div className="onb-tl-clip" ref={clipRef}>
          <canvas ref={cvRef} role="img" aria-label="The timeline of Europe Has a Problem: 5,487 clips on 30 tracks, 50 minutes"></canvas>
        </div>
        <span className="onb-head" ref={headRef} aria-hidden="true">
          <span className="onb-read"><b ref={countRef}>5,487</b> clips <i></i> <span ref={tcRef}>50:23</span></span>
        </span>
        <div className="onb-tl-fade" aria-hidden="true"></div>
      </div>

      <div className="onb-in">
        <div className="onb-legend" role="group" aria-label="Show one layer">
          {LAYERS.map((l) => (
            <button
              key={l.g} type="button" className="onb-chip" aria-pressed={pinned === l.g}
              onPointerEnter={(e) => { if (e.pointerType === 'mouse') setLit(l.g); }} onPointerLeave={() => setLit(null)}
              onFocus={() => setLit(l.g)} onBlur={() => setLit(null)}
              onClick={() => setPinned((p) => (p === l.g ? null : l.g))}
            >
              <i style={{ background: GROUP_COLOR[l.g] }}></i>{l.label}<b>{l.n}</b>
            </button>
          ))}
        </div>

        <div className="onb-txt">
          <h1 className="onb-h1">
            <span className="onb-mask"><span className="onb-line onb-num">5,487 clips.</span></span>
            <span className="onb-mask"><span className="onb-line crw-h-grad">Seven days of work.</span></span>
            <span className="onb-mask"><span className="onb-line crw-h-grad">One editor.</span></span>
          </h1>
          <div className="onb-side">
            <p className="onb-sub">This is one 50-minute documentary. We build yours the same way, from your voiceover.</p>
            <div className="onb-ctas">
              <a href="#contact" onClick={go('#contact')} style={{ textDecoration: 'none', display: 'flex' }}>
                <MetalBtn size="lg" style={{ letterSpacing: '0.08em', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Get a quote</MetalBtn>
              </a>
              <a href="#film" onClick={go('#film')} className="crwh-filmlink" style={{ textDecoration: 'none' }}>
                <span className="crwh-filmlink-icon"><CRWIcon d="play" size={10} /></span>
                Watch the one-minute film
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
